import { ReactElement, useState } from "react"
import {
    areShipmentConfirmationsEqual,
    confirmationsForNonCancelled,
    ShipmentConfirmation,
    ShipmentStatus,
    TransportOrder
} from "../../model/transportorder/transportOrder"
import { FormattedMessage } from "react-intl"
import { routingConfig } from "../Application/RoutingConfig"
import { TransportOrderPresenter } from "./TransportOrderPresenter"
import { canTransportOrderBeAccepted } from "../../model/transportorder/transportOrderEditing"
import { IStickyFooterProps, StickyFooter } from "./TransportOrderFrameFooter"
import { AnnouncementApplicationBanner } from "../Application/AnnouncementApplicationBanner"
import { useLocation, useNavigate, useParams } from "react-router"
import queryString from "query-string"
import { ValidationContextProvider, ValidationContextState } from "../Form/validation/VaildationContext"
import ApplicationLayout from "@rio-cloud/rio-uikit/ApplicationLayout"
import ApplicationLayoutBodyBottomBar from "@rio-cloud/rio-uikit/ApplicationLayoutBodyBottomBar"
import Spinner from "@rio-cloud/rio-uikit/Spinner"
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState"
import Notification from "@rio-cloud/rio-uikit/Notification"
import ErrorState from "@rio-cloud/rio-uikit/ErrorState"
import {
    useGetTransportOrderBundleQuery,
    usePostFreightForwarderResponseMutation,
} from "../../client/transportorder-hub/transportOrderHubApi"
import { OrderOverviewReturnState } from "./useOrderOverviewInfiniteRendering"

enum RenderContentType {
    LOADING_SPINNER,
    NOT_FOUND_ERROR,
    TRANSPORT_ORDER
}

export type TransportOrderId = string

const buildReturnUrl = (search: string): string | undefined => {
    if (!search) {
        return undefined
    }

    const query = queryString.parse(search.substring(1))
    const redirectUri = query["redirect_uri"]
    if (typeof redirectUri === "string") {
        return redirectUri
    } else if (Array.isArray(redirectUri) && redirectUri[0]) {
        return redirectUri[0] || undefined
    } else {
        return undefined
    }
}

export const TransportOrderFrame = (): ReactElement => {
    const transportOrderId = useParams<{ id: string | undefined }>().id ?? ""
    const navigate = useNavigate()
    const location = useLocation()
    const { search } = location
    const returnUrl = buildReturnUrl(search)
    const navigationState = location.state as Partial<OrderOverviewReturnState> | null
    const returnState = typeof navigationState?.visibleCount === "number"
        ? navigationState as OrderOverviewReturnState
        : undefined

    const {
        data: transportOrder,
        isLoading,
        isError,
    } = useGetTransportOrderBundleQuery({ id: transportOrderId }, { skip: transportOrderId === "" })
    if (isError) {
        Notification.error(<FormattedMessage id={"transportOrder.error.not.found"}/>)
    }

    const navigateToPreviousPage = (): void => {
        if (returnUrl) {
            if (returnUrl.startsWith(routingConfig.transportOrderOverview.path)) {
                navigate(returnUrl, returnState ? { state: returnState } : undefined)
            } else {
                window.location.href = returnUrl
            }
        } else {
            navigate(routingConfig.transportOrderOverview.path)
        }
    }

    const contentType = determineRenderContentType(transportOrder, isLoading)

    if (contentType === RenderContentType.TRANSPORT_ORDER && transportOrder) {
        return (
            <LoadedTransportOrderFrame
                key={transportOrder.id}
                navigateToPreviousPage={navigateToPreviousPage}
                transportOrder={transportOrder}
                transportOrderId={transportOrderId}
            />
        )
    }

    return (
        <ApplicationLayout.Body className={"print-area"}>
            {renderNonLoadedContent(contentType)}
        </ApplicationLayout.Body>
    )
}

const LoadedTransportOrderFrame = (props: {
    navigateToPreviousPage: () => void
    transportOrder: TransportOrder
    transportOrderId: TransportOrderId
}): ReactElement => {
    const [confirmationValidationContextState] = useState(() => new ValidationContextState())
    const [editableConfirmations, setEditableConfirmations] = useState<ShipmentConfirmation[]>(() => getEditableConfirmations(props.transportOrder))
    const [transportOrderConfirmationsInProgress, setTransportOrderConfirmationsInProgress] = useState<TransportOrderId[]>([])
    const [postFreightForwarderResponseMutation] = usePostFreightForwarderResponseMutation()

    const hasUnconfirmedOrEditedConfirmations = (): boolean => {
        const originalConfirmations = confirmationsForNonCancelled(getEditableConfirmations(props.transportOrder))
        const editedConfirmations = confirmationsForNonCancelled(editableConfirmations)
        const hasUnconfirmedShipments = props.transportOrder._embedded?.shipments.some(
            it => it.status !== ShipmentStatus.CANCELLED && !it.isConfirmed)
        const userHasEditedConfirmations = !areShipmentConfirmationsEqual(originalConfirmations, editedConfirmations)

        return hasUnconfirmedShipments || userHasEditedConfirmations
    }

    const handleAcceptButtonClicked = ()  => {
        const newTransportOrderConfirmationsInProgress = [...transportOrderConfirmationsInProgress, props.transportOrderId]
        setTransportOrderConfirmationsInProgress(newTransportOrderConfirmationsInProgress)
        const shipmentConfirmations = confirmationsForNonCancelled(editableConfirmations)
        const promises = shipmentConfirmations.map(it => postFreightForwarderResponseMutation(it).unwrap())
        const promise = Promise.all(promises)
        promise
            .then(() => {
                Notification.success(<div data-testid={"notification-confirmation-success"}>
                    <FormattedMessage id={"transportOrder.confirmation.success"}/>
                </div>)
            })
            .catch(() => {
                Notification.error(<FormattedMessage id={"transportOrder.error.confirm"}/>)
            })
            .finally(() => {
                setTransportOrderConfirmationsInProgress(transportOrderConfirmationsInProgress.filter(it => it != props.transportOrderId))
            })
    }

    const getStickyFooterProps = (): IStickyFooterProps => {
        return {
            acceptButtonProps: {
                show: canTransportOrderBeAccepted(props.transportOrder),
                onClick: handleAcceptButtonClicked,
                validationContext: confirmationValidationContextState,
                transportOrder: props.transportOrder,
                confirmations: editableConfirmations.map(it => it.confirmation),
                enabled: hasUnconfirmedOrEditedConfirmations() && !(transportOrderConfirmationsInProgress.findIndex(it => it == props.transportOrderId) >= 0)
            },
            backButtonProps: {
                show: true,
                onClick: props.navigateToPreviousPage,
            },
            printButtonProps: {
                show: true,
                onClick: window.print
            }
        }
    }

    return (
        <ValidationContextProvider value={confirmationValidationContextState}>
            <ApplicationLayout.Body className={"print-area"}
                banner={<AnnouncementApplicationBanner/>}
                bottomBar={
                    <ApplicationLayoutBodyBottomBar className={"non-printable"}>
                        <StickyFooter {...getStickyFooterProps()} />
                    </ApplicationLayoutBodyBottomBar>
                }
            >
                <TransportOrderPresenter
                    transportOrder={props.transportOrder}
                    onConfirmationsChange={setEditableConfirmations}
                />
            </ApplicationLayout.Body>
        </ValidationContextProvider>
    )
}

const determineRenderContentType = (transportOrder: TransportOrder | undefined, isLoading: boolean): RenderContentType => {
    if (transportOrder) {
        return RenderContentType.TRANSPORT_ORDER
    } else {
        if (isLoading) {
            return RenderContentType.LOADING_SPINNER
        } else {
            return RenderContentType.NOT_FOUND_ERROR
        }
    }
}

const renderNonLoadedContent = (contentType: RenderContentType): ReactElement => {
    switch (contentType) {
    case RenderContentType.LOADING_SPINNER:
        // TODO: internationalize
        return <LoadingSpinner text={"Loading Transport Order..."}/>
    case RenderContentType.NOT_FOUND_ERROR:
        // TODO: internationalize
        return (<NotFoundState
            headline="Nothing found"
            message="The Transport Order you are looking for could not be found or you don't have access to it."
        />)
    default:
        return (<ErrorState
            headline={<FormattedMessage id={"application.error.state.headline"}/>}
            message={<FormattedMessage id={"application.error.state.message"}/>}
        />)
    }
}

const getEditableConfirmations = (transportOrder: TransportOrder | undefined): ShipmentConfirmation[] => {
    if (!transportOrder) {
        return []
    }
    return (transportOrder._embedded?.shipments ?? [])
        .map(shipment => ({
            transportOrderId: transportOrder.id,
            shipmentId: shipment.id,
            shipmentStatus: shipment.status,
            confirmation: shipment.confirmationDraft,
            version: shipment.version,
        }))
}

export const LoadingSpinner = (props: { text: string }): ReactElement => {
    return (
        <div className={`spinnerInfoBox col-xs-12`}>
            <div className="spinnerIcon">
                <Spinner isDoubleSized={true}/>
            </div>
            <div className="spinnerText">{props.text}</div>
        </div>
    )
}
